import { alpha, createTheme } from '@mui/material/styles';
import { getActionReviewPalette } from './actionReviewTheme';
import './revampThemeBoundary';

/**
 * Visual system shared by the rollout dashboard surfaces. This deliberately
 * lives beside the legacy themes instead of replacing them: production
 * organizations outside the revamp allowlist must retain today's UI exactly.
 */
const revampTheme = createTheme({
  revamp: true,
  spacing: 8,
  shape: { borderRadius: 10 },
  typography: {
    fontFamily: 'Roboto, Arial, sans-serif',
    h1: { fontWeight: 700, letterSpacing: '-0.025em' },
    h2: { fontWeight: 700, letterSpacing: '-0.02em' },
    h3: { fontWeight: 700, letterSpacing: '-0.015em' },
    h4: { fontWeight: 700, letterSpacing: '-0.01em' },
    button: { fontWeight: 600 },
  },
  palette: {
    mode: 'dark',
    actionReview: getActionReviewPalette('dark'),
    primary: {
      light: '#FF8A72',
      main: '#FF6942',
      dark: '#F04923',
      contrastText: '#121315',
    },
    secondary: {
      main: '#B6A8FF',
      dark: '#9B8AFB',
      contrastText: '#121315',
    },
    info: { main: '#84B5FF', contrastText: '#121315' },
    success: { main: '#6DD6A9', contrastText: '#121315' },
    warning: { main: '#F0C96A', contrastText: '#121315' },
    error: { main: '#FF8A80', contrastText: '#121315' },
    background: { default: '#0E0F11', paper: '#17181B' },
    text: { primary: '#F4F4F5', secondary: '#A8ABB3', disabled: '#6F727A' },
    divider: '#2C2E33',
    grey: {
      50: '#1D1F23',
      100: '#202226',
      200: '#24262B',
      300: '#2C2E33',
      400: '#44474F',
      500: '#6F727A',
      600: '#8A8D95',
      700: '#A8ABB3',
      800: '#D5D6D9',
      900: '#F4F4F5',
    },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: '#0E0F11',
          color: '#F4F4F5',
        },
        '::selection': { backgroundColor: '#35201C' },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: '#121315',
          backgroundImage: 'none',
          color: '#F4F4F5',
          borderBottom: '1px solid #27292E',
          boxShadow: 'none',
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundColor: '#121315',
          backgroundImage: 'none',
          color: '#E9EAEC',
          borderColor: '#27292E',
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
        },
        outlined: {
          borderColor: '#2C2E33',
        },
      },
    },
    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        root: {
          minHeight: 36,
          borderRadius: 8,
          textTransform: 'none',
          letterSpacing: 0,
        },
        containedPrimary: {
          boxShadow: '0 1px 2px rgba(16, 24, 40, 0.12)',
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
        },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          minHeight: 40,
          margin: '2px 8px',
          borderRadius: 8,
          '&:hover': { backgroundColor: '#1D1F23' },
          '&.Mui-selected': {
            backgroundColor: '#2E1D19',
            color: '#FFB6A5',
          },
        },
      },
    },
    MuiListItemIcon: {
      styleOverrides: {
        root: { color: 'inherit' },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: { borderRadius: 6, fontWeight: 650 },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          backgroundColor: '#202226',
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: '#6F727A',
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderWidth: 1,
          },
        },
        notchedOutline: { borderColor: '#44474F' },
      },
    },
    MuiTableHead: {
      styleOverrides: {
        root: { backgroundColor: '#1D1F23' },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: { borderColor: '#2C2E33' },
        head: {
          color: '#A8ABB3',
          fontSize: 12,
          fontWeight: 700,
          letterSpacing: '0.04em',
          textTransform: 'uppercase',
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        indicator: { height: 2 },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          minHeight: 44,
          textTransform: 'none',
          fontWeight: 650,
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: { borderRadius: 10 },
        standardInfo: {
          backgroundColor: '#172638',
          color: '#84B5FF',
          border: '1px solid #2C4B70',
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: alpha('#111827', 0.94),
          fontSize: 12,
        },
      },
    },
  },
});

export default revampTheme;
